import { useEffect, useState } from "react";
import { X, Heart } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDate, toUtc } from "@/lib/mood";
import { NoteThread } from "@/components/NoteThread";
import { ThankButton } from "@/components/ThankButton";
import { INBOX_EVENT } from "@/pages/Inbox";
import { logError } from "@/lib/log";

export const SupporterNotes = () => {
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    api.get("/supporter-notes").then((r) => {
      setNotes(r.data);
      const unread = r.data.slice(0, 3).filter((n) => !n.read);
      Promise.all(unread.map((n) => api.post(`/supporter-notes/${n.id}/read`))).then(() => unread.length && window.dispatchEvent(new Event(INBOX_EVENT)))
        .catch((e) => logError("Mark read failed:", e));
    }).catch((e) => logError("Supporter notes failed:", e));
  }, []);

  const heart = async (id) => {
    setNotes((list) => list.map((n) => (n.id === id ? { ...n, hearted: true } : n)));
    try {
      await api.post(`/supporter-notes/${id}/heart`);
      toast.success("Heart sent 💜");
    } catch (e) {
      logError("Heart failed:", e);
      setNotes((list) => list.map((n) => (n.id === id ? { ...n, hearted: false } : n)));
    }
  };

  const dismiss = async (id) => {
    setNotes((list) => list.filter((n) => n.id !== id));
    await api.delete(`/supporter-notes/${id}`).catch((e) => logError("Dismiss failed:", e));
  };

  if (!notes.length) return null;
  return (
    <div className="space-y-3 mb-6" data-testid="home-supporter-notes">
      {notes.slice(0, 3).map((n) => (
        <div key={n.id} className="mm-card p-5 mm-fade-up flex gap-4 items-start" style={{ borderColor: "var(--mm-primary)" }} data-testid={`supporter-note-${n.id}`}>
          <div className="w-10 h-10 rounded-full mm-gradient flex items-center justify-center shrink-0"><Heart size={18} className="fill-current" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-mm-muted">
              {!n.read && <span className="text-mm-primary font-bold mr-1">New ·</span>}
              {n.kind === "hug" ? "A hug from" : "A note from"} <b className="text-mm-text">{n.supporter_name}</b> · {fmtDate(toUtc(n.created_at), { month: "short", day: "numeric" })}
            </div>
            <p className="mt-1 whitespace-pre-wrap break-words">{n.message}</p>
            {n.kind === "hug"
              ? <div className="mt-3"><ThankButton noteId={n.id} index={0} name={n.supporter_name} done={n.thanked?.includes(0)} label="Thank you for the hug 💜"
                  onDone={(d) => setNotes((list) => list.map((x) => (x.id === n.id ? { ...x, thread: d.thread, thanked: d.thanked } : x)))} /></div>
              : <button onClick={() => !n.hearted && heart(n.id)} disabled={n.hearted} data-testid={`supporter-note-heart-${n.id}`}
              className={`mt-3 inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-3 py-1.5 transition-transform ${n.hearted ? "bg-mm-soft text-mm-error" : "mm-soft hover:scale-105"}`}>
              <Heart size={15} className={n.hearted ? "fill-current text-mm-error" : ""} />{n.hearted ? "Heart sent" : "Send a heart back"}
            </button>}
            <NoteThread note={n} me="user" otherName={n.supporter_name} endpoint={`/supporter-notes/${n.id}/reply`}
              onUpdate={(id, thread) => setNotes((list) => list.map((x) => (x.id === id ? { ...x, thread } : x)))} />
          </div>
          <button onClick={() => dismiss(n.id)} className="text-mm-muted hover:text-mm-text" aria-label="Dismiss" data-testid={`supporter-note-dismiss-${n.id}`}><X size={16} /></button>
        </div>
      ))}
    </div>
  );
};
